import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import BinarySelect from '../../../../components/BinarySelect/BinarySelect';
import InputField from '../../../../components/CredentialsInput/InputField';
import InfoCell from '../../../../components/InfoCell/InfoCell';
import SubmitButton from '../../../../components/SubmitButton/SubmitButton';
import useForm from '../../../../customHooks/useForm';
import MyAccountContentHeader from '../MyAccountContentHeader/MyAccountContentHeader';
import * as translator from '../../../../utils/translator';
import * as http from '../../../../utils/http';
import './MyAccountPersonalData.css';

const MyAccountPersonalData = () => {
	const { setActiveMenuLinkId, setStickyNotificationDetails, customer, subscriptionCount, country, language, setError, setLoading } =
		useOutletContext<any>();
	const [deactivateAccount, setDeactivateAccount] = useState(false);
	const navigate = useNavigate();
	const [disabledChangeEmail, setDisabledChangeEmail] = useState(true);
	const [verificationStatusMessage, setVerificationStatusMessage] = useState('address_verified');
	const [personalDetails, setPersonalDetails] = useState<any>(null);

  useEffect(() => {
		setActiveMenuLinkId('personal-data');
		setLoading(true);

		http
			.getFromServer(`${country}/account/personal-details`, [http.MIDDLEWARES.CUSTOMER_ID])
			.then((response) => {
				setPersonalDetails(response.data);
			})
			.catch((error) => {
				window.clientLogger.error('Error in MyAccountPersonalData: ', error);
				setError(500);
			})
			.finally(() => {
				setLoading(false);
			});
	}, []);

	const getVerificationStatusMessageByCode = useCallback(() => {
		switch (verificationStatusMessage) {
			case 'address_needs_to_be_verified':
				return <p id="email-field-verification-status" className="text-sm text-[#facc15]"> {translator.translate(language, 'my_account', `PersonalData/${verificationStatusMessage}`)} </p>;
			case 'address_verified':
				return <p id="email-field-verification-status" className="text-sm text-[#269C6E]"> {translator.translate(language, 'my_account', `PersonalData/${verificationStatusMessage}`)} </p>;
			default:
				return null;
		}
	}, [verificationStatusMessage]);

	const emailField = useForm(
		{
			email: customer?.email || personalDetails?.email,
		},
		language
	);

	const nameFields = useForm(
		{
			first_name: '',
			last_name: '',
		},
		language
	);

	useEffect(() => {
		if (personalDetails) {
			nameFields.overrideForm({
				first_name: personalDetails.firstName || '',
				last_name: personalDetails.lastName || '',
			});
		}
	}, [personalDetails]);

	const onSubmitEmail = () => {
		const currentEmail = (customer?.email || personalDetails?.email || '').trim().toLowerCase();
		const newEmail = (emailField.form['email'] || '').trim().toLowerCase();
		const isFormValid = emailField.validateForm();

		if (!isFormValid || !newEmail) {
			setStickyNotificationDetails((currentDetails: any) => ({ ...currentDetails, show: false }));
			return;
		}

		if (newEmail === currentEmail) {
			setStickyNotificationDetails({
				show: true,
				title: translator.translate(language, 'my_account', `stickyNotifications/error/errorTitle`),
				content: translator.translate(language, 'my_account', `stickyNotifications/error/emailTaken`),
				level: 'error',
				showInfoIcon: true,
			});
			return;
		}

		http
			.postToServer(`account/change-email`, {
				email: customer.email,
				newEmail: emailField.form['email'],
				country,
				language,
				emailType: 'changeEmail',
			}, [http.MIDDLEWARES.CUSTOMER_ID])
			.then((response) => {
				if (response.data == 'email taken') {
					setStickyNotificationDetails({
						show: true,
						title: translator.translate(language, 'my_account', `stickyNotifications/error/errorTitle`),
						content: translator.translate(language, 'my_account', `stickyNotifications/error/emailTaken`),
						level: 'error',
						showInfoIcon: true,
					});
					return;
				}

				if (response.data != 'OK') {
					setStickyNotificationDetails({
						show: true,
						title: '',
						content: translator.translate(language, 'my_account', `stickyNotifications/error/error`),
						level: 'error',
						showInfoIcon: false,
					});
					return;
				}

				setStickyNotificationDetails({
					show: true,
					title: translator.translate(language, 'my_account', `stickyNotifications/success/changeEmailTitle`),
					content: translator.translate(language, 'my_account', `stickyNotifications/success/changeEmail`),
					level: 'additional_action_required',
					showInfoIcon: true,
				});
				setVerificationStatusMessage('address_needs_to_be_verified');
			})
			.catch(() => {
				setStickyNotificationDetails({
					show: true,
					title: '',
					content: translator.translate(language, 'my_account', `stickyNotifications/error/error`),
					level: 'error',
					showInfoIcon: false,
				});
			});
	};

	const onSubmitName = () => {
		nameFields.validateForm();
		if (!nameFields.form['first_name'] || !nameFields.form['last_name']) {
			return;
		}
		http
			.postToServer('account-update/personal-details', {
				firstName: nameFields.form['first_name'],
				lastName: nameFields.form['last_name'],
			}, [http.MIDDLEWARES.CUSTOMER_ID])
			.then(() => {
				setStickyNotificationDetails({
					show: true,
					title: '',
					content: translator.translate(language, 'my_account', `stickyNotifications/success/personalDetails`),
					level: 'success',
					showInfoIcon: true,
				});
			})
			.catch(() =>
				setStickyNotificationDetails({
					show: true,
					title: '',
					content: translator.translate(language, 'my_account', `stickyNotifications/error/error`),
					level: 'error',
					showInfoIcon: false,
				})
			);
	};

	useEffect(() => {
		if (deactivateAccount) {
			if (subscriptionCount > 0) {
				navigate(`/${country}/account/orders-subscription`);
				setStickyNotificationDetails({
					show: true,
					title: '',
					content: translator.translate(language, 'my_account', `stickyNotifications/error/deactivateAccount`),
					level: 'error',
					showInfoIcon: false,
				});
				window.scrollTo(0, 0);
			} else {
				http
					.postToServer(`account/deactivate-account`, { email: customer.email, country, language, emailType: 'deactivateAccount' }, [http.MIDDLEWARES.CUSTOMER_ID])
					.then((response) => {
						setStickyNotificationDetails({
							show: true,
							title: translator.translate(language, 'my_account', `stickyNotifications/success/deactivateAccountTitle`),
							content: translator.translate(language, 'my_account', `stickyNotifications/success/deactivateAccount`),
							level: 'error',
							showInfoIcon: true,
						});
					})
					.catch(
						setStickyNotificationDetails({
							show: true,
							title: '',
							content: translator.translate(language, 'my_account', `stickyNotifications/error/error`),
							level: 'error',
							showInfoIcon: false,
						})
					);
			}
		}
	}, [deactivateAccount]);

	const verificationTranslation = getVerificationStatusMessageByCode();

  if (!personalDetails) {
    return <></>
  }

	return (
		<>
			<div className="account-content">
				<MyAccountContentHeader
					title={translator.translate(language, 'my_account', `PersonalData/title`)}
					description={translator.translate(language, 'my_account', `PersonalData/subTitle`)}
				/>

				<div className="email-form-container">
					<InputField
						mandatory={true}
						disabled={disabledChangeEmail}
						id="email-field"
						placeholder={translator.translate(language, 'my_account', `forms/email`)}
						value={emailField.form['email']}
						errorMsg={emailField.errorMessagePerField['email']}
						language={language}
						onBlur={() => emailField.onFieldBlur('email')}
						touched={emailField.touched.email}
						onChange={(value: any) => {
							emailField.updateForm('email', value);
						}}
						childrenIcon={
							<>
								{disabledChangeEmail && !personalDetails.emailChangeDisabled && (
									<button
										type="button"
										id="change-email-button"
										className="change-email-button"
										onClick={() => {
											setDisabledChangeEmail(false);
											setVerificationStatusMessage('');
										}}
									>
										Change
									</button>
								)}

								{verificationTranslation}
							</>
						}
					/>

					{!disabledChangeEmail && (
						<div id="email-submit-container" className="flex justify-end mt-[8px]">
							<SubmitButton
								onSubmit={onSubmitEmail}
								id={'email-submit'}
								placeholder={translator.translate(language, 'my_account', `DeliveryAddress/submit`)}
							/>
						</div>
					)}
				</div>

				<div className="personal-data-form">
					<div className="info-cells-row">
						<span className="info-cell-title">{translator.translate(language, 'my_account', `PersonalData/birthday`)}</span>
						<InfoCell title={''} value={personalDetails.birthday.day} />
						<InfoCell title={''} value={personalDetails.birthday.month} />
						<InfoCell title={''} value={personalDetails.birthday.year} />
					</div>

					<div className="info-cells-row-2">
						<span className="info-cell-title"> {translator.translate(language, 'my_account', `PersonalData/gender`)} </span>
						<BinarySelect
							locked
							valueLeft={translator.translate(language, 'my_account', `PersonalData/man`)}
							valueRight={translator.translate(language, 'my_account', `PersonalData/woman`)}
							valuePicked={
								personalDetails.gender === 1
									? translator.translate(language, 'my_account', `PersonalData/man`)
									: translator.translate(language, 'my_account', `PersonalData/woman`)
							}
						/>
					</div>

					<div className="info-cells-row-3">
						<InputField
							mandatory={true}
							id="first-name-field"
							placeholder={translator.translate(language, 'my_account', `PersonalData/firstName`)}
							value={nameFields.form['first_name']}
							errorMsg={nameFields.errorMessagePerField['first_name']}
							language={language}
							onBlur={() => nameFields.onFieldBlur('first_name')}
							touched={nameFields.touched.first_name}
							onChange={(value: any) => {
								nameFields.updateForm('first_name', value);
							}}
						/>
						<InputField
							mandatory={true}
							id="last-name-field"
							placeholder={translator.translate(language, 'my_account', `PersonalData/lastName`)}
							value={nameFields.form['last_name']}
							errorMsg={nameFields.errorMessagePerField['last_name']}
							language={language}
							onBlur={() => nameFields.onFieldBlur('last_name')}
							touched={nameFields.touched.last_name}
							onChange={(value: any) => {
								nameFields.updateForm('last_name', value);
							}}
						/>
					</div>

					<div className="delivery-address-last-row">
						<span
							className="deactivate-account"
							onClick={() => {
								setDeactivateAccount(true);
							}}
						>
							{translator.translate(language, 'my_account', `PersonalData/deactivate`)}
						</span>
						<SubmitButton
							onSubmit={onSubmitName}
							id={'name-submit'}
							placeholder={translator.translate(language, 'my_account', `PersonalData/submit`)}
						/>
					</div>
				</div>
			</div>
		</>
	);
};

export default MyAccountPersonalData;